import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Fab from "@mui/material/Fab";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import Title from "../components/header/Title";
import AddressFormDialog from "../components/forms/AddressFormDialog";
import UserService from "../services/UserService";
import IUser from "../entities/IUser";
import IAddress from "../entities/IAddress";
import { unwrapList, getErrorMessage } from "../services/apiUtils";
import { useSnackbar } from "../hooks/useSnackbar";

const userLabel = (u: IUser) =>
  u.name || [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.username || `User #${u.userId}`;

const Addresses: React.FC = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | "">("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<IAddress | null>(null);
  const { snackbar, showSuccess, showError, close } = useSnackbar();

  const loadUsers = () => {
    setLoading(true);
    UserService.getUsers()
      .then((res: any) => setUsers(unwrapList<IUser>(res)))
      .catch((err: any) => showError(getErrorMessage(err, "Could not load users.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();

  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => u.userId === selectedUserId) || null,
    [users, selectedUserId]
  );
  const addresses = selectedUser?.addressList || [];

  const openAddDialog = () => {
    setEditingAddress(null);
    setDialogOpen(true);
  };

  const openEditDialog = (address: IAddress) => {
    setEditingAddress(address);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Title title="Addresses" subtitle="View and manage a customer's saved addresses" />

      <TextField
        select
        fullWidth
        label="Select User"
        value={selectedUserId}
        onChange={(e) => setSelectedUserId(e.target.value === "" ? "" : Number(e.target.value))}
        sx={{ mb: 3, maxWidth: 480 }}
      >
        {users.map((u) => (
          <MenuItem key={u.userId} value={u.userId}>
            {userLabel(u)}
          </MenuItem>
        ))}
      </TextField>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : selectedUserId === "" ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
          <GroupOutlinedIcon sx={{ fontSize: 70, color: "grey.300" }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Please select a user to view addresses
          </Typography>
        </Box>
      ) : addresses.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          No addresses found for this user.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {addresses.map((addr) => (
            <Card key={addr.addressId} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <LocationOnRoundedIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={600}>{addr.address1 || "No Address Detail"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[addr.cityName, addr.district].filter(Boolean).join(", ")}
                  </Typography>
                </Box>
                <IconButton color="primary" onClick={() => openEditDialog(addr)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {selectedUserId !== "" && (
        <Fab color="primary" onClick={openAddDialog} sx={{ position: "fixed", bottom: 32, right: 32 }}>
          <AddRoundedIcon />
        </Fab>
      )}

      <AddressFormDialog
        open={dialogOpen}
        userId={selectedUserId === "" ? null : Number(selectedUserId)}
        address={editingAddress}
        onClose={() => setDialogOpen(false)}
        onSaved={loadUsers}
        onError={showError}
        onSuccess={showSuccess}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={close}>
        <Alert severity={snackbar.severity} onClose={close}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Addresses;
